import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components'
import { useTeams } from './context/TeamsContext'
import { useConfig } from './context/ConfigContext'
import { getAzureTenantId, getSsoToken } from './lib/api'
import { Layout } from './components/Layout'
import { SubmitTicket } from './pages/SubmitTicket'
import { MyTickets } from './pages/MyTickets'
import { TicketDetail } from './pages/TicketDetail'
import { AdminDashboard } from './pages/AdminDashboard'
import { AdminTicketDetail } from './pages/AdminTicketDetail'
import { Setup } from './pages/Setup'
import { Loading } from './components/Loading'
import { ErrorState } from './components/ErrorState'

function App() {
  const { isInitialized, theme, hasSsoToken, error: teamsError } = useTeams()
  const { isLoading, error, refetch } = useConfig()
  const [authReady, setAuthReady] = useState(!!getAzureTenantId() || !!getSsoToken())

  // Re-check auth after Teams context initializes
  useEffect(() => {
    if (isInitialized && !authReady) {
      const ready = !!getAzureTenantId() || !!getSsoToken() || hasSsoToken
      if (ready) setAuthReady(true)
    }
  }, [isInitialized, authReady, hasSsoToken])

  // Refetch config when credentials become available (after Setup or Teams context init)
  useEffect(() => {
    if (authReady) {
      refetch()
    }
  }, [authReady, refetch])

  // Select theme based on Teams context
  const fluentTheme =
    theme === 'dark'
      ? teamsDarkTheme
      : theme === 'contrast'
        ? teamsHighContrastTheme
        : teamsLightTheme

  // No auth context yet. Show the setup page.
  if (!authReady) {
    return (
      <FluentProvider theme={fluentTheme}>
        <Setup />
      </FluentProvider>
    )
  }

  if (!isInitialized || isLoading) {
    return (
      <FluentProvider theme={fluentTheme}>
        <Loading message="Loading support portal..." />
      </FluentProvider>
    )
  }

  if (teamsError && !hasSsoToken) {
    return (
      <FluentProvider theme={fluentTheme}>
        <ErrorState
          title="Unable to Connect"
          message={teamsError}
          onRetry={() => window.location.reload()}
        />
      </FluentProvider>
    )
  }

  if (error) {
    return (
      <FluentProvider theme={fluentTheme}>
        <ErrorState
          title="Unable to Connect"
          message={error}
          onRetry={() => window.location.reload()}
        />
      </FluentProvider>
    )
  }

  return (
    <FluentProvider theme={fluentTheme}>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/submit" replace />} />
          <Route path="/submit" element={<SubmitTicket />} />
          <Route path="/tickets" element={<MyTickets />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/tickets/:id" element={<AdminTicketDetail />} />
        </Routes>
      </Layout>
    </FluentProvider>
  )
}

export default App
